// DashboardSidebar — reusable sidebar component for all dashboard-shell pages.
// Import this instead of writing sidebar markup inline in each page.
import React, {ReactNode, useEffect, useRef, useState} from 'react';
import ReactDOM from 'react-dom';
import {Link} from 'react-router-dom';
import {useSettings} from '@common/core/settings/use-settings';
import {useIsDarkMode} from '@common/ui/themes/use-is-dark-mode';
import {useThemeSelector} from '@common/ui/themes/theme-selector-context';
import {useAuth} from '@common/auth/use-auth';
import {useLogout} from '@common/auth/requests/logout';
import {DashboardWorkspaceSelector} from './dashboard-workspace-selector';
import {useAccountUsage} from '@app/editor/use-account-usage';
import {openBuyAiUsageDialog} from '@app/editor/ai/usage/buy-ai-usage-dialog';
import {useAiTopupCheckoutReturn} from '@app/editor/ai/usage/use-ai-topup-checkout-return';
import './dashboard-sidebar.css';

export type DashSidebarItem =
  | 'home'
  | 'all'
  | 'published'
  | 'templates'
  | 'domains'
  | 'aiUsage'
  | 'settings'
  | 'billing'
  | 'invoices';

interface DashboardSidebarProps {
  /** Which nav item should be highlighted. */
  activeItem?: DashSidebarItem;
  /**
   * Called when a view-switching nav item (home / all / published) is clicked.
   * If not provided those items render as <Link to="/dashboard">.
   */
  onNavClick?: (item: DashSidebarItem) => void;
  /** Called when the search button in the sidebar header is clicked. */
  onSearchOpen?: () => void;
  /**
   * Optional custom content to replace the default nav + footer block
   * (everything below the divider). Use this when embedding the sidebar in
   * non-dashboard pages that need different navigation items.
   */
  navChildren?: ReactNode;
}

export function DashboardSidebar({
  activeItem,
  onNavClick,
  onSearchOpen,
  navChildren,
}: DashboardSidebarProps) {
  useAiTopupCheckoutReturn();

  const {branding} = useSettings();
  const {data: usage} = useAccountUsage();
  const isDarkMode = useIsDarkMode();
  const logo = isDarkMode ? branding.logo_light : branding.logo_dark;
  const [logoMenuOpen, setLogoMenuOpen] = useState(false);

  // Render a nav item as a button (in-page navigation) or a Link (cross-page).
  function NavItem({
    id,
    label,
    href,
    children,
  }: {
    id: DashSidebarItem;
    label: string;
    href?: string;
    children: React.ReactNode;
  }) {
    const isActive = activeItem === id;
    const cls = `db-nav-item${isActive ? ' db-nav-item--active' : ''}`;
    const isViewSwitcher = id === 'home' || id === 'all' || id === 'published';

    if (onNavClick && isViewSwitcher) {
      return (
        <button type="button" className={cls} onClick={() => onNavClick(id)}>
          {children}
          {label}
        </button>
      );
    }
    return (
      <Link to={href ?? '/dashboard'} className={cls}>
        {children}
        {label}
      </Link>
    );
  }

  return (
    <aside className="db-sidebar">
      {/* Logo + search */}
      <div className="db-sidebar__top">
        <button
          type="button"
          className="db-sidebar__logo-btn"
          onClick={() => setLogoMenuOpen(v => !v)}
          aria-label="Open menu"
        >
          {logo ? (
            <img src={logo} alt={branding.site_name} className="db-sidebar__logo" />
          ) : (
            <span className="db-sidebar__wordmark">{branding.site_name}</span>
          )}
        </button>
        {logoMenuOpen && (
          <LogoMenu onClose={() => setLogoMenuOpen(false)} />
        )}
        <button
          type="button"
          className="db-sidebar__search-btn"
          aria-label="Search"
          onClick={onSearchOpen}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
          </svg>
        </button>
      </div>

      {/* Workspace selector */}
      <div className="db-sidebar__ws">
        <DashboardWorkspaceSelector />
      </div>

      {/* Action buttons */}
      <div className="db-sidebar__actions">
        <Link to="/dashboard/new" className="db-sidebar__action-btn">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <path d="M12 5v14M5 12h14"/>
          </svg>
          Create something new
        </Link>
        <Link to="/dashboard/templates" className="db-sidebar__action-btn">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
            <polyline points="17 8 12 3 7 8"/>
            <line x1="12" y1="3" x2="12" y2="15"/>
          </svg>
          Import or use template
        </Link>
      </div>

      <div className="db-sidebar__divider" />

      {navChildren ?? (
        <>
          {/* Nav */}
          <nav className="db-sidebar__nav">
        <NavItem id="home" label="Home">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
            <polyline points="9 22 9 12 15 12 15 22"/>
          </svg>
        </NavItem>

        <NavItem id="all" label="Projects">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <rect x="3" y="3" width="7" height="7" rx="1"/>
            <rect x="14" y="3" width="7" height="7" rx="1"/>
            <rect x="3" y="14" width="7" height="7" rx="1"/>
            <rect x="14" y="14" width="7" height="7" rx="1"/>
          </svg>
        </NavItem>

        <NavItem id="published" label="Published Projects">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <circle cx="12" cy="12" r="10"/>
            <line x1="2" y1="12" x2="22" y2="12"/>
            <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/>
          </svg>
        </NavItem>

        <NavItem id="templates" label="Templates" href="/dashboard/templates">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <path d="M12 2L2 7l10 5 10-5-10-5z"/>
            <path d="M2 17l10 5 10-5"/>
            <path d="M2 12l10 5 10-5"/>
          </svg>
        </NavItem>

        <NavItem id="domains" label="Domains" href="/dashboard/domains">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/>
            <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
          </svg>
        </NavItem>

        <NavItem id="aiUsage" label="AI Usage" href="/account-settings?panel=ai-usage">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 3L4 7v10l8 4 8-4V7z"/>
            <path d="M12 12l8-5"/>
            <path d="M12 12l-8-5"/>
            <path d="M12 12v9"/>
          </svg>
        </NavItem>

        <NavItem id="settings" label="Settings" href="/account-settings">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <circle cx="12" cy="12" r="3"/>
            <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
          </svg>
        </NavItem>
      </nav>

      {/* Footer */}
      <SidebarAiUsageCard usage={usage} />
      <div className="db-sidebar__foot">
        <a
          href="https://docs.example.com"
          target="_blank"
          rel="noreferrer"
          className="db-nav-item db-nav-item--foot"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <circle cx="12" cy="12" r="10"/>
            <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          Learn
        </a>
        <a
          href="https://docs.example.com"
          target="_blank"
          rel="noreferrer"
          className="db-nav-item db-nav-item--foot"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <path d="M4 19.5A2.5 2.5 0 016.5 17H20"/>
            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/>
          </svg>
          Documentation
        </a>
      </div>
        </>
      )}
    </aside>
  );
}

function LogoMenu({onClose}: {onClose: () => void}) {
  const {user} = useAuth();
  const logout = useLogout();
  const isDark = useIsDarkMode();
  const {selectedTheme, selectTheme} = useThemeSelector();
  const {notifications} = useSettings();
  const unreadCount = (user as any)?.unread_notifications_count ?? 0;
  const ref = useRef<HTMLDivElement>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [submenuPos, setSubmenuPos] = useState<{top: number; left: number} | null>(null);
  const helpRef = useRef<HTMLDivElement>(null);
  const helpTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const themeLabel = isDark ? 'Dark' : 'Light';

  function openHelp() {
    if (helpTimer.current) clearTimeout(helpTimer.current);
    if (helpRef.current) {
      const r = helpRef.current.getBoundingClientRect();
      setSubmenuPos({top: r.top, left: r.right + 6});
    }
    setHelpOpen(true);
  }
  function closeHelp() { helpTimer.current = setTimeout(() => setHelpOpen(false), 120); }

  // Close on outside click or Escape
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  return (
    <div className="db-logo-menu" ref={ref} role="menu">
      {/* Settings */}
      <Link to="/account-settings" className="db-logo-menu__item" onClick={onClose} role="menuitem">
        <span className="db-logo-menu__icon">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
            <circle cx="12" cy="12" r="3"/>
            <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
          </svg>
        </span>
        <span className="db-logo-menu__label">Settings</span>
      </Link>

      {/* Notifications */}
      {notifications?.integrated && (
        <Link to="/notifications" className="db-logo-menu__item" onClick={onClose} role="menuitem">
          <span className="db-logo-menu__icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
              <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
              <path d="M13.73 21a2 2 0 01-3.46 0"/>
            </svg>
          </span>
          <span className="db-logo-menu__label">Notifications</span>
          {unreadCount > 0 && (
            <span className="db-logo-menu__badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
          )}
        </Link>
      )}

      <div className="db-logo-menu__sep" />

      {/* Theme toggle */}
      {selectedTheme && (
        <button
          type="button"
          className="db-logo-menu__item"
          onClick={() => { selectTheme(isDark ? 'light' : 'dark'); onClose(); }}
          role="menuitem"
        >
          <span className="db-logo-menu__icon">
            {isDark ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/>
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                <circle cx="12" cy="12" r="5"/>
                <line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
              </svg>
            )}
          </span>
          <span className="db-logo-menu__label">Theme</span>
          <span className="db-logo-menu__meta">{themeLabel}</span>
        </button>
      )}

      {/* Help (with submenu flyout) */}
      <div
        className="db-logo-menu__help-wrap"
        ref={helpRef}
        onMouseEnter={openHelp}
        onMouseLeave={closeHelp}
      >
        <button
          type="button"
          className={`db-logo-menu__item${helpOpen ? ' db-logo-menu__item--active' : ''}`}
          role="menuitem"
          aria-haspopup="true"
          aria-expanded={helpOpen}
        >
          <span className="db-logo-menu__icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
              <circle cx="12" cy="12" r="10"/>
              <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          </span>
          <span className="db-logo-menu__label">Help</span>
          <span className="db-logo-menu__meta db-logo-menu__meta--chevron">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M9 18l6-6-6-6"/></svg>
          </span>
        </button>

        {helpOpen && submenuPos && ReactDOM.createPortal(
          <div
            className="db-logo-submenu"
            style={{position: 'fixed', top: submenuPos.top, left: submenuPos.left, zIndex: 9999}}
            onMouseEnter={openHelp}
            onMouseLeave={closeHelp}
            role="menu"
          >
            <a href="https://status.piksera.com" target="_blank" rel="noreferrer" className="db-logo-submenu__item" onClick={onClose} role="menuitem">
              <span className="db-logo-submenu__icon">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                </svg>
              </span>
              Status
            </a>
            <a href="https://docs.piksera.com/help" target="_blank" rel="noreferrer" className="db-logo-submenu__item" onClick={onClose} role="menuitem">
              <span className="db-logo-submenu__icon">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
              </span>
              Get help
            </a>
            <a href="https://community.piksera.com" target="_blank" rel="noreferrer" className="db-logo-submenu__item" onClick={onClose} role="menuitem">
              <span className="db-logo-submenu__icon">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="2" y1="12" x2="22" y2="12"/>
                  <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10A15.3 15.3 0 0112 2z"/>
                </svg>
              </span>
              Community Hub
            </a>
            <a href="https://piksera.com/changelog" target="_blank" rel="noreferrer" className="db-logo-submenu__item" onClick={onClose} role="menuitem">
              <span className="db-logo-submenu__icon">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
              </span>
              View updates
            </a>
            <a href="https://docs.piksera.com" target="_blank" rel="noreferrer" className="db-logo-submenu__item" onClick={onClose} role="menuitem">
              <span className="db-logo-submenu__icon">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
                  <path d="M4 19.5A2.5 2.5 0 016.5 17H20"/>
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/>
                </svg>
              </span>
              Read the docs
            </a>
          </div>,
          document.body
        )}
      </div>

      <div className="db-logo-menu__sep" />

      {/* Log out */}
      <button
        type="button"
        className="db-logo-menu__item db-logo-menu__item--danger"
        onClick={() => { logout.mutate(); onClose(); }}
        role="menuitem"
        disabled={logout.isPending}
      >
        <span className="db-logo-menu__icon">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
            <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
            <polyline points="16 17 21 12 16 7"/>
            <line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
        </span>
        <span className="db-logo-menu__label">Log out</span>
      </button>
    </div>
  );
}

function SidebarAiUsageCard({usage}: {usage: any}) {
  if (!usage?.ai) return null;

  const text = usage.ai.text;
  const images = usage.ai.images;

  const textPercent = toPercent(text?.used, text?.total);
  const imagePercent = toPercent(images?.used, images?.total);

  return (
    <div className="db-ai-usage">
      <div className="db-ai-usage__title">AI usage</div>

      <UsageRow
        label="Text credits"
        detail={formatUsage(text?.used, text?.total, 'tokens')}
        percent={textPercent}
      />

      <UsageRow
        label="Image credits"
        detail={formatUsage(images?.used, images?.total, 'tokens')}
        percent={imagePercent}
      />

      <Link to="/account-settings?panel=ai-usage" className="db-ai-usage__cta">
        View full AI usage
      </Link>
      <button
        type="button"
        className="db-ai-usage__buy"
        disabled={usage.ai.topups?.enabled === false}
        onClick={() =>
          openBuyAiUsageDialog({
            returnPath: `${window.location.pathname}${window.location.search}`,
          })
        }
      >
        Buy extra usage
      </button>
      <Link to="/pricing" className="db-ai-usage__upgrade">
        Upgrade plan
      </Link>
    </div>
  );
}

function UsageRow({
  label,
  detail,
  percent,
}: {
  label: string;
  detail: string;
  percent: number;
}) {
  return (
    <div className="db-ai-usage__row">
      <div className="db-ai-usage__meta">
        <div className="db-ai-usage__label">{label}</div>
        <div className="db-ai-usage__detail">{detail}</div>
      </div>
      <div className="db-ai-usage__bar">
        <div className="db-ai-usage__fill" style={{width: `${percent}%`}} />
      </div>
    </div>
  );
}

function toPercent(used?: number, total?: number | null): number {
  const usedValue = used ?? 0;
  if (!total || total <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((usedValue / total) * 100)));
}

function formatUsage(
  used?: number,
  total?: number | null,
  unit: 'tokens' | 'requests' = 'tokens',
): string {
  const usedLabel = (used || 0).toLocaleString();
  if (!total || total <= 0) {
    return `${usedLabel} ${unit} used`;
  }

  const pct = toPercent(used, total);
  return `${pct}% used · ${usedLabel}/${total.toLocaleString()}`;
}

// ═══════════════════════════════════════════════════════════════════════════
//  BOTTOM NAV — rendered on mobile (≤767px) in place of the sidebar
// ═══════════════════════════════════════════════════════════════════════════
export type BottomNavTab = 'apps' | 'create' | 'account';

interface DashboardBottomNavProps {
  /** Which tab is currently active. */
  activeTab: BottomNavTab;
  /** Called when the user taps the "Apps" tab (index view switching). */
  onAppsClick?: () => void;
}

/**
 * Mobile bottom navigation bar (Replit-style).
 * Renders fixed at the bottom on small screens; hidden on desktop via CSS.
 */
export function DashboardBottomNav({activeTab, onAppsClick}: DashboardBottomNavProps) {
  const appsActive   = activeTab === 'apps';
  const createActive = activeTab === 'create';
  const acctActive   = activeTab === 'account';

  return (
    <nav className="db-bottom-nav" aria-label="Main navigation">
      {/* Apps tab */}
      {onAppsClick ? (
        <button
          type="button"
          className={`db-bottom-nav__item${appsActive ? ' db-bottom-nav__item--active' : ''}`}
          onClick={onAppsClick}
          aria-label="Apps"
        >
          <span className="db-bottom-nav__icon">
            {appsActive ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                <path d="M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z" rx="1"/>
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                <rect x="3" y="3" width="7" height="7" rx="1"/>
                <rect x="14" y="3" width="7" height="7" rx="1"/>
                <rect x="3" y="14" width="7" height="7" rx="1"/>
                <rect x="14" y="14" width="7" height="7" rx="1"/>
              </svg>
            )}
          </span>
          Apps
        </button>
      ) : (
        <Link
          to="/dashboard"
          className={`db-bottom-nav__item${appsActive ? ' db-bottom-nav__item--active' : ''}`}
          aria-label="Apps"
        >
          <span className="db-bottom-nav__icon">
            {appsActive ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                <rect x="3" y="3" width="7" height="7" rx="1"/>
                <rect x="14" y="3" width="7" height="7" rx="1"/>
                <rect x="3" y="14" width="7" height="7" rx="1"/>
                <rect x="14" y="14" width="7" height="7" rx="1"/>
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                <rect x="3" y="3" width="7" height="7" rx="1"/>
                <rect x="14" y="3" width="7" height="7" rx="1"/>
                <rect x="3" y="14" width="7" height="7" rx="1"/>
                <rect x="14" y="14" width="7" height="7" rx="1"/>
              </svg>
            )}
          </span>
          Apps
        </Link>
      )}

      {/* Create tab */}
      <Link
        to="/dashboard/new"
        className={`db-bottom-nav__item${createActive ? ' db-bottom-nav__item--active' : ''}`}
        aria-label="Create"
      >
        <span className="db-bottom-nav__icon">
          {createActive ? (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
              <circle cx="12" cy="12" r="9" fill="currentColor" stroke="none" style={{opacity:.12}}/>
              <path d="M12 8v8M8 12h8"/>
            </svg>
          ) : (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <circle cx="12" cy="12" r="9"/>
              <path d="M12 8v8M8 12h8"/>
            </svg>
          )}
        </span>
        Create
      </Link>

      {/* Account tab */}
      <Link
        to="/account-settings"
        className={`db-bottom-nav__item${acctActive ? ' db-bottom-nav__item--active' : ''}`}
        aria-label="Account"
      >
        <span className="db-bottom-nav__icon">
          {acctActive ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="none">
              <path d="M12 12a5 5 0 100-10 5 5 0 000 10zm0 2c-5.33 0-8 2.67-8 4v1h16v-1c0-1.33-2.67-4-8-4z"/>
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
          )}
        </span>
        Account
      </Link>
    </nav>
  );
}
